import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { LegalPage, H2, H3, P, UL, Callout } from '../components/LegalPage';

const LeitpalelPrivacy = () => {
  const { lang } = useLanguage();

  if (lang === 'en') {
    return (
      <LegalPage
        testid="leitpalel-privacy-en"
        eyebrow="Leitpalel for Dummies"
        title="Privacy Policy"
        subtitle="Operated by Inversiones Tair SpA"
        lastUpdated="Last updated: February 2026"
      >
        <P>
          At <strong className="text-white">Leitpalel for Dummies</strong> (the "App"), operated by{' '}
          <strong className="text-white">Inversiones Tair SpA</strong> (tair.site), we deeply respect your
          privacy. This policy explains in plain words what data we collect, why, and what rights you have.
        </P>

        <Callout
          title="Quick summary"
          items={[
            'We do not sell your data. Ever.',
            'Microphone and speech recognition run on your device. We do not upload your voice.',
            'Your location is used only to compute halachic prayer times (zmanim). It is not stored.',
            'If you create an account, we store your email and name for authentication.',
          ]}
        />

        <H2>1. Data we collect</H2>
        <H3>1.1 Data you provide</H3>
        <UL items={[
          <><strong className="text-white">Account:</strong> name and email at signup.</>,
          <><strong className="text-white">Preferences:</strong> language, rite (Sefardi/Ashkenazi), font size, Name style.</>,
          <><strong className="text-white">Support:</strong> emails you send us.</>,
        ]} />
        <H3>1.2 Data generated by use</H3>
        <UL items={[
          <><strong className="text-white">Learning progress:</strong> which prayers you visited.</>,
          <><strong className="text-white">In-app purchases:</strong> subscription status handled by Apple.</>,
          <><strong className="text-white">Basic diagnostics:</strong> app version, device model, OS and anonymous crash logs.</>,
        ]} />
        <H3>1.3 Data we DO NOT collect</H3>
        <UL items={[
          'We do not record your voice.',
          'We do not store precise GPS coordinates.',
          'We do not access calendar, photos, contacts or files.',
          'No ads, no tracking cookies.',
        ]} />

        <H2>2. Device permissions</H2>
        <UL items={[
          <><strong className="text-white">Microphone:</strong> Synagogue Mode only. Processed locally and discarded.</>,
          <><strong className="text-white">Speech Recognition:</strong> handled by Apple under their policy.</>,
          <><strong className="text-white">Location (in-use):</strong> only to compute zmanim. Never sent to our servers.</>,
          <><strong className="text-white">Face ID / Touch ID:</strong> optional, fully managed by Apple.</>,
        ]} />

        <H2>3. How we use data</H2>
        <UL items={[
          'Deliver the correct prayers for your rite and language.',
          'Auto-detect the right prayer based on halachic time.',
          'Remember your learning progress.',
          'Validate your premium subscription or free trial.',
          'Answer your support requests.',
          'Fix crashes via anonymous logs.',
        ]} />

        <H2>4. Who we share data with</H2>
        <UL items={[
          <><strong className="text-white">Apple</strong> — for in-app payments and speech recognition.</>,
          <><strong className="text-white">Hebcal API</strong> — for halachic times (approximate city coordinate only).</>,
          <><strong className="text-white">MongoDB Atlas / Railway</strong> — encrypted account storage.</>,
          <><strong className="text-white">AI services</strong> — to generate transliterations and narration. No personal data sent.</>,
        ]} />

        <H2>5. Data retention</H2>
        <P>
          We keep your account while active. You may request deletion at{' '}
          <a href="mailto:info@tair.site" className="text-amber-300 hover:text-amber-200">info@tair.site</a>.
          Removal happens within 30 days.
        </P>

        <H2>6. Children</H2>
        <P>The App is not directed to children under 13.</P>

        <H2>7. Your rights</H2>
        <UL items={[
          'Access your data.',
          'Request correction or deletion.',
          'Cancel account and subscription.',
          'Withdraw consent by uninstalling.',
        ]} />
        <P>
          Contact:{' '}
          <a href="mailto:info@tair.site" className="text-amber-300 hover:text-amber-200">info@tair.site</a>
        </P>

        <H2>8. Changes</H2>
        <P>We will announce material changes in the App. The "Last updated" date will reflect the version.</P>

        <H2>9. Contact</H2>
        <P>
          Controller: <strong className="text-white">Inversiones Tair SpA</strong> ·{' '}
          <a href="mailto:info@tair.site" className="text-amber-300 hover:text-amber-200">info@tair.site</a>
        </P>
      </LegalPage>
    );
  }

  // Spanish (default)
  return (
    <LegalPage
      testid="leitpalel-privacy-es"
      eyebrow="Leitpalel for Dummies"
      title="Política de Privacidad"
      subtitle="Operada por Inversiones Tair SpA"
      lastUpdated="Última actualización: Febrero 2026"
    >
      <P>
        En <strong className="text-white">Leitpalel for Dummies</strong> (en adelante, "la App"), operada
        por <strong className="text-white">Inversiones Tair SpA</strong> (tair.site), respetamos
        profundamente tu privacidad. Esta política explica de forma sencilla qué datos recopilamos, para
        qué los usamos y qué derechos tienes sobre ellos.
      </P>

      <Callout
        title="Resumen rápido"
        items={[
          'No vendemos tus datos. Punto.',
          'El micrófono y el reconocimiento de voz se procesan en tu dispositivo. No subimos tu voz a nuestros servidores.',
          'Tu ubicación solo se usa para calcular los horarios halájicos de rezo (zmanim). No la guardamos.',
          'Si creas cuenta, guardamos tu email y nombre para autenticarte.',
        ]}
      />

      <H2>1. Datos que recopilamos</H2>
      <H3>1.1 Datos que tú nos das</H3>
      <UL items={[
        <><strong className="text-white">Cuenta:</strong> nombre y correo electrónico cuando te registras.</>,
        <><strong className="text-white">Preferencias:</strong> idioma, nusaj (sefaradí/ashkenazí), tamaño de letra y estilo de Nombre divino.</>,
        <><strong className="text-white">Soporte:</strong> mensajes que nos envías por correo electrónico.</>,
      ]} />
      <H3>1.2 Datos generados por el uso de la App</H3>
      <UL items={[
        <><strong className="text-white">Progreso de aprendizaje:</strong> rezos visitados, para mostrarte tu historial. No hay rachas ni rankings.</>,
        <><strong className="text-white">Compras dentro de la app:</strong> el estado de tu suscripción gestionado por Apple. No vemos tu tarjeta de crédito.</>,
        <><strong className="text-white">Datos técnicos básicos:</strong> versión de la App, modelo de dispositivo, sistema operativo y errores no personales (crash logs).</>,
      ]} />
      <H3>1.3 Datos que NO recopilamos</H3>
      <UL items={[
        'No grabamos ni guardamos audio de tu voz.',
        'No guardamos coordenadas GPS exactas.',
        'No accedemos a tu agenda, fotos, contactos ni archivos.',
        'No usamos publicidad ni cookies de tracking.',
      ]} />

      <H2>2. Permisos del dispositivo</H2>
      <UL items={[
        <><strong className="text-white">Micrófono:</strong> requerido sólo para el "Modo Sinagoga" (reconocimiento de voz en tiempo real). El audio se procesa localmente en tu iPhone/iPad y se descarta inmediatamente.</>,
        <><strong className="text-white">Reconocimiento de voz (Speech Recognition):</strong> Apple procesa el habla para identificar palabras hebreas. Apple aplica su propia política de privacidad.</>,
        <><strong className="text-white">Ubicación (mientras usas la App):</strong> sólo para calcular los zmanim halájicos de tu ciudad (Shajarit, Minjá, Arvit). La ubicación nunca se envía a nuestros servidores.</>,
        <><strong className="text-white">Face ID / Touch ID:</strong> opcional, para iniciar sesión rápida. Los datos biométricos los maneja Apple, nunca los vemos.</>,
      ]} />

      <H2>3. Cómo usamos tus datos</H2>
      <UL items={[
        'Proporcionarte los rezos correctos según tu rito y idioma.',
        'Calcular automáticamente el rezo apropiado según la hora halájica.',
        'Recordar tu progreso de aprendizaje en tu cuenta.',
        'Validar tu suscripción premium o periodo de prueba gratuito.',
        'Responder a tus consultas de soporte.',
        'Mejorar la estabilidad de la App con crash logs anónimos.',
      ]} />

      <H2>4. Con quién compartimos datos</H2>
      <P>Con nadie con fines comerciales. Sólo con los proveedores estrictamente necesarios:</P>
      <UL items={[
        <><strong className="text-white">Apple</strong> — para procesar pagos in-app y reconocimiento de voz.</>,
        <><strong className="text-white">Hebcal API</strong> — para horarios halájicos (sólo se envía coordenada aproximada de tu ciudad, no tu identidad).</>,
        <><strong className="text-white">MongoDB Atlas / Railway</strong> — almacenamiento seguro de tu cuenta (cifrado en reposo y en tránsito).</>,
        <><strong className="text-white">Servicios de IA</strong> — para generar transliteraciones y narración de textos religiosos. Ningún dato personal se envía a estos servicios.</>,
      ]} />

      <H2>5. Retención de datos</H2>
      <P>
        Mantenemos tu cuenta mientras esté activa. Puedes solicitar la eliminación total escribiendo a{' '}
        <a href="mailto:info@tair.site" className="text-amber-300 hover:text-amber-200">info@tair.site</a>.
        Tu cuenta será borrada en un plazo máximo de 30 días.
      </P>

      <H2>6. Niños</H2>
      <P>La App no está dirigida a menores de 13 años y no recopilamos información de niños deliberadamente.</P>

      <H2>7. Tus derechos</H2>
      <P>Puedes en cualquier momento:</P>
      <UL items={[
        'Acceder a los datos que tenemos sobre ti.',
        'Solicitar su corrección o eliminación.',
        'Cancelar tu cuenta y suscripción.',
        'Retirar tu consentimiento (al desinstalar la App).',
      ]} />
      <P>
        Para ejercerlos:{' '}
        <a href="mailto:info@tair.site" className="text-amber-300 hover:text-amber-200">info@tair.site</a>
      </P>

      <H2>8. Cambios en esta política</H2>
      <P>
        Si actualizamos esta política, te avisaremos dentro de la App. La fecha de "Última actualización"
        siempre estará al inicio de este documento.
      </P>

      <H2>9. Contacto</H2>
      <P>
        Responsable: <strong className="text-white">Inversiones Tair SpA</strong>
      </P>
      <P>
        Correo:{' '}
        <a href="mailto:info@tair.site" className="text-amber-300 hover:text-amber-200">info@tair.site</a>
      </P>
      <P>
        Sitio: <a href="https://tair.site/que-hacemos/desarrollo-software" className="text-amber-300 hover:text-amber-200">tair.site/que-hacemos/desarrollo-software</a>
      </P>
    </LegalPage>
  );
};

export default LeitpalelPrivacy;
